import React, {useEffect, useState} from "react";
import {useCurrentUser} from "../../../../../../contexts/UserContext";
import {Button, Divider, Flex, message, Select} from "antd";
import {FileTextIcon} from "@phosphor-icons/react";
import {useViewerStore} from "../../../../../../store/store";
import {BsShieldFillCheck, BsShieldFillExclamation, BsShieldLockFill} from "react-icons/bs";
import siteService from "../../../../../../services/siteService"
import {getDesignVersionList} from '../../../../../../services/designVersionService';
import {CertificationStatus} from "../../../../../../domain/models/core/CertificationStatus";


interface CertificationOption {
    value: string | number;
    label: string;
    icon: React.ReactNode;
    enabled: boolean;
}

const CertificationSection : React.FC = () => {
    const {Option} = Select;
    const sceneViewer = useViewerStore(state => state.sceneViewer);

    const {hasCapability} = useCurrentUser();
    const {designList, selectedDesignIndex, masterDataList} = useViewerStore(state => state);
    const [certification, setCertification] = useState(designList[selectedDesignIndex]?.Design_Certification_Status ? getValueByLabel(designList[selectedDesignIndex].Design_Certification_Status) : undefined)
    const [cerOption, setCerOption] = useState<CertificationOption[] | undefined>()
    const [enableEditCer] = useState(hasCapability("Pre_Installed_Certified") || hasCapability("Install_Change_Certified") || hasCapability("As_Built_Certified") || hasCapability("Certified_Design_Override"))
    const [buttonDisable, setButtonDisable] = useState(false)
    const [cerLoader, setCerLoader] = useState(false)
    const stage = sessionStorage.getItem('currentStage');
    const [messageApi, contextHolder] = message.useMessage();

    const alertMessage = (type : string, content : any) => {
        messageApi.open({
            //type:type,
            content: content,
            className:
                type === "error"
                    ? "alertError"
                    : type === "success"
                        ? "alertSuccess"
                        : "",
        });
    };


    function getValueByLabel(label : string) {
        if( !masterDataList?.DesigCertificationStatus) return
        for (const item of masterDataList?.DesigCertificationStatus) {
            if (item.label === label) {
                return item.value;
            }
        }
        return undefined;
    }

    useEffect(() => {
        if (designList[selectedDesignIndex]?.Design_Certification_Status && (getValueByLabel(designList[selectedDesignIndex].Design_Certification_Status) !== certification)) {
            setButtonDisable(true)
        } else {
            setButtonDisable(false)
        }
    }, [certification])

    useEffect(() => {
        if (selectedDesignIndex >= 0) {
            setCertificationOptions()
            setCertification(designList[selectedDesignIndex] && designList[selectedDesignIndex].Design_Certification_Status ? getValueByLabel(designList[selectedDesignIndex].Design_Certification_Status) : undefined)
        }
    }, [selectedDesignIndex, designList])

    const setCertificationOptions = () => {
        if (!masterDataList?.DesigCertificationStatus) {
            setCerOption([]);          // early exit – nothing to show
            return;
        }

        const options: CertificationOption[] =
            masterDataList.DesigCertificationStatus.map(item => {
                /* ✅ 1) start with a fully-typed skeleton                */
                const opt: CertificationOption = {
                    value: item.value,
                    label: item.label,
                    icon: null,
                    enabled: false,
                };

                /* ✅ 2) fill in the details per status */
                switch (item.value) {
                    case CertificationStatus.PreInstallCertified:
                        opt.icon = (
                            <BsShieldFillCheck
                                color={
                                    Number(stage) < 4 || Number(stage) > 10 || !enableEditCer
                                        ? ''
                                        : '#dadada'
                                }
                            />
                        );
                        opt.enabled =
                            Number(stage) >= 4 &&
                            Number(stage) <= 7 &&
                            hasCapability('Pre_Installed_Certified');
                        break;

                    case CertificationStatus.InstallChangeCertified:
                        opt.icon = (
                            <BsShieldFillCheck
                                color={
                                    Number(stage) < 4 || Number(stage) > 10 || !enableEditCer
                                        ? ''
                                        : '#ffb300'
                                }
                            />
                        );
                        opt.enabled =
                            hasCapability('Install_Change_Certified') &&
                            (Number(stage) === 7 ||
                                designList[selectedDesignIndex]?.Design_Certification_Status ===
                                'Pre-Install Certified');
                        break;

                    case CertificationStatus.AsBuiltCertified:
                        opt.icon = (
                            <BsShieldLockFill
                                color={
                                    Number(stage) < 4 || Number(stage) > 10 || !enableEditCer
                                        ? ''
                                        : '#5aac5a'
                                }
                            />
                        );
                        opt.enabled =
                            hasCapability('As_Built_Certified') &&
                            ((Number(stage) >= 7 && Number(stage) <= 10) ||
                                designList[selectedDesignIndex]?.Design_Certification_Status ===
                                'Pre-Install Certified' ||
                                designList[selectedDesignIndex]?.Design_Certification_Status ===
                                'Install Change Certified');
                        break;

                    case CertificationStatus.CertifiedDesignOverride:
                        opt.icon = (
                            <BsShieldFillExclamation
                                color={
                                    Number(stage) < 4 || Number(stage) > 10 || !enableEditCer
                                        ? ''
                                        : '#ca4a4a'
                                }
                            />
                        );
                        opt.enabled =
                            hasCapability('Certified_Design_Override') &&
                            Number(stage) >= 4 &&
                            Number(stage) <= 10 &&
                            ['Pre-Install Certified', 'Install Change Certified', 'As-Built Certified'].includes(
                                designList[selectedDesignIndex]?.Design_Certification_Status || '',
                            );
                        break;
                }

                return opt;
            });

        setCerOption(options);
    };



    const handleCertification = (value : any) => {
        setCertification(value)
    }
    const handlrCertify = async () => {
        setCerLoader(true)
        const response = await siteService.saveDesignCertification(String(designList[selectedDesignIndex].DesignID), Number(certification));
        if (response === "Design Certification Status Updated Successfully") {
            const SalesForceId = sessionStorage.getItem('SalesForceId');
            const designList = await getDesignVersionList(String(SalesForceId));
            if (designList === undefined || designList === null) {
                alertMessage("error", "Something went wrong. Please try later.");
                setCerLoader(false)
                return;
            }
            useViewerStore.setState({designList: designList});
            alertMessage("success", "Design Certification Status Updated Successfully");
        } else {
            alertMessage("error", "Something went wrong. Please try later.");
        }
        setCerLoader(false)

    }


    return (

        <Flex vertical gap={20} style={{width: "90%"}}>
            {selectedDesignIndex >= 0 && (
                <>
                    {contextHolder}
                    <Flex justify={'left'} align={'center'} gap={5} className={"sidebar-headings"}> <FileTextIcon
                        size={32}/>Design Certification</Flex>
                    <Select
                        size="large"
                        placeholder="Select Certification Status"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option && option.props && option.props.children &&
                            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        value={certification ? certification : "Not Certified"}
                        onChange={(value) => handleCertification(value)}
                        disabled={Number(stage) < 4 || Number(stage) > 10 || !enableEditCer}
                    >
                        {cerOption?.map((option : CertificationOption , index : number) => {
                            return (
                                <Option value={option.value} key={index}
                                        disabled={!option.enabled}>{option.label} {option.icon}</Option>
                            )
                        })}
                    </Select>
                    <Button type='primary' size='middle' block onClick={handlrCertify}
                            disabled={Number(stage) < 4 || !buttonDisable || certification === undefined || !enableEditCer}
                            loading={cerLoader}>Certify</Button>
                </>
            )
            }
        </Flex>

    )
}

export default CertificationSection;